import {faCheckCircle} from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import {useFormikContext} from 'formik';
import {useCallback, useState} from 'react';
import styled from 'styled-components';

import {FormButtonWrapper} from '../../_old/Button/ButtonWrapper';
import {Icon} from '../../components/Icon/Icon';
import {Text} from '../../components/Text';
import {scToTwColour} from '../../theme';

import {gotoValidationError} from './functions';
import {useSubmitValidationErrorHandler} from './useSubmitValidationErrorHandler';

import type {Tab} from './functions';

type Props = {
  tabs: Record<string, Tab>;
  tabsToRender?: string[];
  onCancel: (() => void) | (() => Promise<void>);
  extraButtons?: React.ReactNode;
  onTabChange?: (tabIndex: React.SetStateAction<number>) => void;
};

export function TabbedForm({
  tabs,
  tabsToRender,
  onCancel,
  extraButtons,
  onTabChange,
}: Props): React.JSX.Element {
  const [tabIndex, setTabIndex] = useState(0);
  const tabList = tabsToRender ?? Object.keys(tabs);
  const maxTabs = tabList.length;

  const gotoTab = useCallback<React.Dispatch<React.SetStateAction<number>>>(
    (idx) => {
      setTabIndex(idx);
      onTabChange?.(idx);
    },
    [onTabChange]
  );
  const previousTab = useCallback(() => {
    gotoTab((tabIndex) => Math.max(tabIndex - 1, 0));
  }, [gotoTab]);
  const nextTab = useCallback(() => {
    gotoTab((tabIndex) => Math.min(tabIndex + 1, maxTabs - 1));
  }, [gotoTab, maxTabs]);

  const {isSubmitting, errors} = useFormikContext();

  const handler = useCallback(() => {
    gotoValidationError(tabs, tabList, errors, setTabIndex);
  }, [errors, tabList, tabs]);
  useSubmitValidationErrorHandler(handler);

  const active = tabs[tabList[tabIndex]];

  return (
    <>
      <Header width={active.width}>
        {tabList.map((id, i) => (
          <TabHeading
            key={id}
            label={tabs[id].label}
            currentIndex={tabIndex}
            index={i}
            tabsCount={tabList.length}
            setTabIndex={gotoTab}
          />
        ))}
      </Header>

      <FormContentWrapper width={active.width}>
        {active.content}

        <FormButtonWrapper isSubmitting={isSubmitting}>
          {tabIndex < maxTabs - 1 ? (
            <button
              key="next"
              className="btn-secondary"
              type="button"
              onClick={nextTab}
            >
              Next
            </button>
          ) : typeof extraButtons !== 'undefined' ? (
            extraButtons
          ) : (
            <button key="submit" className="btn-main" type="button">
              <Icon icon={faCheckCircle} />
              Save
            </button>
          )}

          {tabIndex > 0 ? (
            <button
              key="back"
              className="btn-muted"
              type="button"
              onClick={previousTab}
            >
              Back
            </button>
          ) : (
            <button
              key="cancel"
              className="btn-muted"
              type="button"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </FormButtonWrapper>
      </FormContentWrapper>
    </>
  );
}

// padding is for horizontal scroll
const defaultWidth = '40em';
const Header = styled.header<{width?: string}>`
  padding-bottom: 1em;
  margin-bottom: 1em;

  ${({theme, width}) => theme.media.md`
    display: flex;
    align-items: center;
    max-width: ${width ?? defaultWidth};
    overflow-x: auto;
  `}
`;

export const FormContentWrapper = styled.section<{width?: string}>`
  max-width: ${({width}) => width ?? defaultWidth};
`;

type HeadingProps = {
  label: string;
  index: number;
  tabsCount: number;
  currentIndex: number;
  setTabIndex: (n: number) => void;
};
function TabHeading({
  label,
  index,
  tabsCount,
  currentIndex,
  setTabIndex,
}: HeadingProps): React.JSX.Element {
  const tabCn = clsx(index === currentIndex ? 'bg-blue10' : 'bg-slate9');

  return (
    <>
      <HeadingButton
        type="button"
        onClick={() => {
          setTabIndex(index);
        }}
      >
        <TabIndicator className={tabCn}>{index + 1}</TabIndicator>

        <Text as="strong" className="whitespace-nowrap">
          {label}
        </Text>
      </HeadingButton>

      {index !== tabsCount - 1 && <Divider className="border-slate7" />}
    </>
  );
}
const HeadingButton = styled.button`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  text-align: center;
  background-color: transparent;
  padding: 0;
  margin: 0;

  &:focus {
    outline: 0;
  }
`;
const Divider = styled.div`
  height: 1px;
  border-top: 1px solid;
  margin: 0.5em 0;

  ${({theme}) => theme.media.md`
    min-width: 1em;
    margin: 0 1em;
  `}
`;
const TabIndicator = styled.span`
  display: inline-block;
  width: 2em;
  line-height: 2em;
  border-radius: 50%;
  margin-right: 0.5em;
  color: ${scToTwColour('theme.colors.white')};
  text-align: center;
`;
